import { OWNER_LIST_LOADING_HTML } from '../../core/dom.js';
import { apiFetchJSON } from '../../core/api.js';
import { buildPresetSpecs, escapeAttr } from '../../core/format.js';
import { getPortfolioDraft, setPortfolioDraftKind } from './draft.js';
import { showPortfolioDetail } from './detail.js';
import type {
  FetchPortfolioOptions,
  PortfolioApiResponse,
  PortfolioItem,
  PortfolioKindFilter,
  PortfolioStats
} from '../../types/api.js';

let lastPortfolioList: PortfolioItem[] = [];
let portfolioFilterKind: PortfolioKindFilter = 'all';
let portfolioStatsCache: PortfolioStats | null = null;

export async function fetchPortfolio(opts?: FetchPortfolioOptions): Promise<PortfolioItem[]> {
  try {
    const data = await apiFetchJSON<PortfolioApiResponse | null>('/api/portfolio').catch(
      () => null
    );
    if (!data || !data.ok || !Array.isArray(data.items)) return [];
    if (data.stats) portfolioStatsCache = data.stats;
    const items = data.items;
    if (opts?.includeHidden) return items;
    return items.filter((item) => !item?.hidden);
  } catch {
    return [];
  }
}

function portfolioKindLabel(kind: string): string {
  if (kind === 'catalog') return 'Готовая';
  if (kind === 'upgrade') return 'Апгрейд';
  return 'На заказ';
}

function portfolioStarsHtml(rating: number | string | undefined, max = 5): string {
  const n = Math.max(0, Math.min(max, parseInt(String(rating), 10) || 0));
  let out = '';
  for (let i = 1; i <= max; i++) out += i <= n ? '★' : '☆';
  return out;
}

function portfolioSpecSummary(item: PortfolioItem | null | undefined): string {
  const specs = buildPresetSpecs(item?.specs ? item.specs : item);
  const parts = [specs.cpu, specs.gpu, specs.ram].filter(Boolean);
  return parts.join(' · ');
}

export function readPortfolioReviewFromForm(): {
  author: string;
  rating: number;
  text: string;
  source: string;
} | null {
  const authorEl = document.getElementById('portfolio-step-review-author') as HTMLInputElement | null;
  const textEl = document.getElementById('portfolio-step-review-text') as HTMLTextAreaElement | null;
  const author = (authorEl?.value || '').trim();
  const text = (textEl?.value || '').trim();
  if (!text) return null;
  const draft = getPortfolioDraft();
  return {
    author: author || 'Покупатель',
    rating: draft?.review?.rating ? draft.review.rating : 5,
    text,
    source: 'manual'
  };
}

export function setPortfolioRatingPicker(rating: number | string): void {
  const wrap = document.getElementById('portfolio-step-rating-picker');
  if (!wrap) return;
  const n = Math.max(1, Math.min(5, parseInt(String(rating), 10) || 5));
  wrap.querySelectorAll('.portfolio-rating-star').forEach((btn) => {
    const r = parseInt(btn.getAttribute('data-rating') || '', 10);
    btn.classList.toggle('is-active', r <= n);
  });
}

export function bindPortfolioRatingPicker(): void {
  const wrap = document.getElementById('portfolio-step-rating-picker');
  if (!wrap || wrap.getAttribute('data-bound') === '1') return;
  wrap.setAttribute('data-bound', '1');
  wrap.querySelectorAll('.portfolio-rating-star').forEach((btn) => {
    btn.addEventListener('click', () => {
      const n = parseInt(btn.getAttribute('data-rating') || '', 10) || 5;
      const draft = getPortfolioDraft();
      if (!draft.review) draft.review = { author: '', rating: 5, text: '' };
      draft.review.rating = n;
      setPortfolioRatingPicker(n);
    });
  });
}

export function bindPortfolioKindPicker(): void {
  const wrap = document.getElementById('portfolio-step-kind-picker');
  if (!wrap || wrap.getAttribute('data-bound') === '1') return;
  wrap.setAttribute('data-bound', '1');
  wrap.querySelectorAll('.portfolio-kind-chip').forEach((btn) => {
    btn.addEventListener('click', () => {
      const kind = btn.getAttribute('data-kind') || 'custom';
      setPortfolioDraftKind(kind);
      wrap.querySelectorAll('.portfolio-kind-chip').forEach((b) => {
        b.classList.toggle('is-active', b.getAttribute('data-kind') === kind);
      });
    });
  });
}

export function bindPortfolioFilterBar(): void {
  const bar = document.getElementById('portfolio-filter-bar');
  if (!bar || bar.getAttribute('data-bound') === '1') return;
  bar.setAttribute('data-bound', '1');
  bar.querySelectorAll('.portfolio-filter-chip').forEach((btn) => {
    btn.addEventListener('click', () => {
      portfolioFilterKind = (btn.getAttribute('data-kind') || 'all') as PortfolioKindFilter;
      bar.querySelectorAll('.portfolio-filter-chip').forEach((b) => {
        b.classList.toggle('is-active', b.getAttribute('data-kind') === portfolioFilterKind);
      });
      renderPortfolioList(lastPortfolioList);
    });
  });
}

function updatePortfolioHero(stats?: PortfolioStats | null): void {
  const hero = document.getElementById('portfolio-hero');
  if (!hero) return;
  const s = stats || portfolioStatsCache;
  if (!s?.total) {
    hero.hidden = true;
    return;
  }
  hero.hidden = false;
  const works = document.getElementById('portfolio-stat-works');
  const reviews = document.getElementById('portfolio-stat-reviews');
  const ratingWrap = document.getElementById('portfolio-stat-rating-wrap');
  const rating = document.getElementById('portfolio-stat-rating');
  if (works) works.textContent = String(s.total || 0);
  if (reviews) reviews.textContent = String(s.reviews || 0);
  if (ratingWrap && rating) {
    if ((s.reviews ?? 0) > 0 && s.avgRating != null) {
      ratingWrap.hidden = false;
      rating.textContent = String(s.avgRating);
    } else {
      ratingWrap.hidden = true;
    }
  }
}

function renderPortfolioCard(item: PortfolioItem): string {
  const title = escapeAttr(item.title || '');
  const kind = String(item.kind || 'custom');
  let imgs = Array.isArray(item.images) ? item.images.filter(Boolean) : [];
  if (!imgs.length && item.image) imgs = [item.image];
  const img = imgs[0] || '';
  const specLine = portfolioSpecSummary(item);
  const review = item.review?.text ? item.review : null;
  let html =
    '<article class="preset-announcement-card" data-portfolio-id="' +
    escapeAttr(item.id) +
    '" tabindex="0" role="button">';
  html += '<div class="preset-card-image-wrap">';
  html +=
    '<span class="portfolio-card-kind portfolio-card-kind--' +
    escapeAttr(kind) +
    '">' +
    escapeAttr(portfolioKindLabel(kind)) +
    '</span>';
  if (img) html += '<img src="' + escapeAttr(img) + '" alt="" />';
  else {
    html +=
      '<div style="width:100%;height:100%;background:#242424;display:flex;align-items:center;justify-content:center;color:#8e8e93;font-size:12px;">Нет фото</div>';
  }
  html += '</div>';
  html += '<div class="preset-card-info">';
  html += '<p class="preset-card-title">' + title + '</p>';
  if (specLine) html += '<p class="portfolio-card-spec-line">' + escapeAttr(specLine) + '</p>';
  if (review) {
    html +=
      '<div class="portfolio-card-stars" aria-label="Оценка ' +
      escapeAttr(String(review.rating || 5)) +
      ' из 5">' +
      portfolioStarsHtml(review.rating, 5) +
      '</div>';
  } else {
    html += '<p class="portfolio-card-stars-muted">Без отзыва</p>';
  }
  html += '</div></article>';
  return html;
}

function renderPortfolioList(items: PortfolioItem[]): void {
  const listEl = document.getElementById('portfolio-list');
  if (!listEl) return;
  const filtered = (items || []).filter((item) => {
    if (!item || item.hidden) return false;
    if (portfolioFilterKind === 'all') return true;
    return String(item.kind || 'custom') === portfolioFilterKind;
  });
  if (!filtered.length) {
    listEl.classList.remove('preset-list-loading');
    listEl.classList.add('preset-list-empty');
    listEl.innerHTML = '<p class="profile-empty">Пока нет работ в этой категории.</p>';
    return;
  }
  listEl.classList.remove('preset-list-loading');
  listEl.classList.remove('preset-list-empty');
  listEl.innerHTML = filtered.map(renderPortfolioCard).join('');
  listEl.querySelectorAll('[data-portfolio-id]').forEach((card) => {
    const id = card.getAttribute('data-portfolio-id');
    const item = (items || []).find((p) => String(p.id) === String(id));
    if (!item) return;
    const portfolioItem = item;
    function openDetail(): void {
      showPortfolioDetail(portfolioItem);
    }
    card.addEventListener('click', openDetail);
    card.addEventListener('keydown', (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Enter' || ke.key === ' ') {
        ke.preventDefault();
        openDetail();
      }
    });
  });
}

export async function loadPublicPortfolio(): Promise<void> {
  const listEl = document.getElementById('portfolio-list');
  if (!listEl) return;
  listEl.classList.add('preset-list-loading');
  listEl.classList.remove('preset-list-empty');
  listEl.innerHTML = OWNER_LIST_LOADING_HTML;
  const items = await fetchPortfolio();
  lastPortfolioList = items || [];
  updatePortfolioHero(portfolioStatsCache);
  if (!items.length) {
    listEl.classList.remove('preset-list-loading');
    listEl.classList.add('preset-list-empty');
    listEl.innerHTML =
      '<p class="profile-empty">Пока нет работ. Скоро здесь появятся наши сборки.</p>';
    return;
  }
  renderPortfolioList(lastPortfolioList);
}
